import { FloatMath } from "../math/FloatMath"

export type RoundingFunction = (number: number) => number
export enum RoundingMode {
  ROUND = "round",
  TRUNC = "trunc",
  FLOOR = "floor",
  CEIL = "ceil",
}
export enum RoundingStrategy {
  DEFAULT = "DEFAULT",
  HALF_UP = "HALF_UP",
  HALF_EVEN = "HALF_EVEN",
  HALF_DOWN = "HALF_DOWN",
}
// 默认四舍五入（JS、Java规则）
const DefaultRounding: RoundingFunction = (number) => Math.round(number)

// HALF_UP 四舍五入规则（MySQL）
const HalfUpRounding: RoundingFunction = (number) =>
  number >= 0 ? Math.round(number) : Math.ceil(number - 0.5)

// HALF_DOWN 四舍五入规则（修改后）
const HalfDownRounding: RoundingFunction = (number) => {
  return number <= 0 ? Math.round(number) : Math.ceil(number - 0.5)
}

// HALF_EVEN 银行家算法四舍五入规则
const HalfEvenRounding: RoundingFunction = (number) => {
  const floor = Math.floor(number)
  const diff = number - floor
  return diff > 0.5 || (diff === 0.5 && floor % 2 !== 0) ? floor + 1 : floor
}

export class RoundingHelper {
  #_strategies: { [key in RoundingStrategy]: RoundingFunction } = {
    [RoundingStrategy.DEFAULT]: DefaultRounding,
    [RoundingStrategy.HALF_UP]: HalfUpRounding,
    [RoundingStrategy.HALF_EVEN]: HalfEvenRounding,
    [RoundingStrategy.HALF_DOWN]: HalfDownRounding,
  }
  round(num: number, strategy: string = RoundingStrategy.DEFAULT): number {
    const strategyFunction = this.#_strategies?.[strategy as RoundingStrategy]
    return strategyFunction?.(num)
  }
  /**
   * money 辅币抹零处理
   * @param {TMoney} money money类
   * @param {Object} configObj 配置项集
   * @param {String} configObj.mode 模式: "auto"|"floor"|"ceil"
   * @param {Number} configObj.offset 抹零偏移量: 0~14
   * @param {String} configObj.interval 间隔区间: "unit"|"half_unit"|"quarter_unit"
   * @param {String} configObj.strategy 精度方式: "default"|"half_down"|"half_up"
   * @returns {TMoney} money
   */
  // https://alidocs.dingtalk.com/i/nodes/ydxXB52LJq73XRO0tAweGZPqWqjMp697
  around(money: TMoney, config: {
    mode?: string
    offset?: number
    interval?: "unit" | "half_unit" | "quarter_unit"
    strategy?: "default" | "half_down" | "half_up"
  } = {
      mode: 'auto',
      offset: 0,
      interval: 'unit',
      strategy: 'default'
    }) {
    const {
      mode = 'auto',
      interval = 'unit',
      strategy = 'default'
    } = config
    let offset = config.offset || 0

    // 参数有效性校验
    if (!money?.currency) return new Error("[around]: First params not TMoney")
    if (offset < 0) return new Error("[around]: Offset must be greater than 0")
    if (interval === 'quarter_unit' && offset < 2) return new Error("[around]: When interval is quarter_unit, offset must be greater than or equal to 2")
    const amount = money.amount
    // const numLen = Math.abs(amount).toString().length
    if (offset > 14) offset = 0

    // 根据 offset 计算有效位缩放系数
    const scale = Math.pow(10, offset)
    const scaledValue = amount / scale // 将高位转换为小数部分

    // 根据 interval 选择舍入间隔因子
    let intervalFactor
    switch (interval) {
      case 'unit':
        intervalFactor = 1
        break
      case 'half_unit':
        intervalFactor = 2
        break
      case 'quarter_unit':
        intervalFactor = 4
        break
      default:
        throw new Error("[around]: Unsupported rounding interval types")
    }

    // 核心舍入逻辑
    let processedValue
    switch (mode) {
      case 'ceil':
        processedValue = Math.ceil(scaledValue * intervalFactor) / intervalFactor
        break
      case 'floor':
        processedValue = Math.floor(scaledValue * intervalFactor) / intervalFactor
        break
      case 'auto': {
        const strategyObj = {
          'default': DefaultRounding,
          'half_down': HalfDownRounding,
          'half_up': HalfUpRounding,
          'half_even': HalfEvenRounding
        }
        processedValue = strategyObj[strategy](scaledValue * intervalFactor) / intervalFactor
        break
      }
      default:
        throw new Error("[around]: Unsupported rounding mode")
    }

    // 恢复原始高位并抹零
    return {
      amount: Math.trunc(processedValue * scale),
      currency: money.currency
    }
  }
  _toFixed(num: number, precision: number, round: string) {
    if (num === 0) return 0
    const multiplier = 10 ** precision
    const ans = FloatMath.multiply(num, multiplier)
    // !!! 兼容一下旧的实现
    const adjustedNum = round !== RoundingStrategy.DEFAULT
      ? this.round(ans, round)
      : (num < 0 ? Math.ceil(ans) : this.round(ans, round))
    return +(FloatMath.divide(adjustedNum, multiplier)).toFixed(precision)
  }
  convertAmount(
    amount: number,
    {
      precision,
      cent,
      round,
    }: { precision: number; cent: number; round?: string }
  ) {
    // !!! 特殊处理，按照整数位的位数进行Fixed
    // amount转小数，fixed 再转整数
    const precisionOf10 = Math.pow(10, precision)
    amount = amount / precisionOf10
    const ans = this._toFixed(amount, cent, round)
    return this.round?.(ans * precisionOf10, round)
  }
  processNumber(n: number, mode: TRound, round: string): number {
    if (!["round", "floor", "ceil"].includes(mode))
      throw new Error(
        'processNumber mode must be ["round", "floor", "ceil"]. ' + mode
      )
    let processedNum
    switch (mode) {
      case RoundingMode.ROUND:
        processedNum = this.round?.(n, round)
        break
        // 暂时没有业务场景先注释掉
        break
      case RoundingMode.FLOOR:
        processedNum = Math.floor(n)
        break
      case RoundingMode.CEIL:
        processedNum = Math.ceil(n)
        break
      default:
        throw new Error("Invalid rounding mode: " + mode)
    }

    return processedNum
  }
}
